/**
 * Singleton socket.io connection for the admin back-office.
 *
 * Connects once, joins the `admin` room via JWT, and lets any composable
 * subscribe to lightweight admin events (store:*, organization:*, product:*, user:*).
 *
 * The socket URL is derived from VITE_API_URL — the backend socket server
 * lives on the same origin as the REST API (same NestJS process).
 */
import { io, Socket } from 'socket.io-client';
import { ref, watch } from 'vue';
import { token } from '@/composables/useTokenCookie';
import { VITE_API_URL } from '@/utils/runtimeConfig';

// ── Event envelope ────────────────────────────────────────────────────────────
export type AdminEventEntity = 'store' | 'organization' | 'product' | 'user';
export type AdminEventAction = 'created' | 'updated' | 'deleted';

export interface AdminEvent {
  action: AdminEventAction;
  entity: AdminEventEntity;
  id: string;
  organizationId?: string | null;
  actorId?: string | null;
  at: string; // ISO timestamp
}

type EventName =
  | 'store:created'
  | 'store:updated'
  | 'store:deleted'
  | 'organization:created'
  | 'organization:updated'
  | 'organization:deleted'
  | 'product:created'
  | 'product:updated'
  | 'product:deleted'
  | 'user:created'
  | 'user:updated'
  | 'user:deleted';

type Listener = (event: AdminEvent) => void;

// ── Singleton state ───────────────────────────────────────────────────────────
let _socket: Socket | null = null;
const _listeners = new Map<EventName, Set<Listener>>();

/** Reactive connection status — true once the socket is connected */
export const socketConnected = ref(false);

/** Last received event (useful for debugging / live indicators) */
export const lastAdminEvent = ref<AdminEvent | null>(null);

// ── Socket URL ────────────────────────────────────────────────────────────────
function socketUrl(): string {
  // Strip trailing slash and /api suffix so we hit the socket namespace root.
  return VITE_API_URL.replace(/\/api\/?$/, '').replace(/\/$/, '');
}

// ── Internal helpers ──────────────────────────────────────────────────────────
const ALL_EVENTS: EventName[] = [
  'store:created',
  'store:updated',
  'store:deleted',
  'organization:created',
  'organization:updated',
  'organization:deleted',
  'product:created',
  'product:updated',
  'product:deleted',
  'user:created',
  'user:updated',
  'user:deleted',
];

function broadcast(name: EventName, payload: AdminEvent) {
  lastAdminEvent.value = payload;
  _listeners.get(name)?.forEach((cb) => cb(payload));
}

function attachEvents(s: Socket) {
  ALL_EVENTS.forEach((name) => {
    s.on(name, (payload: AdminEvent) => broadcast(name, payload));
  });
}

function createSocket(accessToken: string): Socket {
  const s = io(socketUrl(), {
    transports: ['websocket'],
    auth: { token: accessToken },
    autoConnect: true,
    reconnection: true,
    reconnectionDelay: 2000,
    reconnectionAttempts: Infinity,
  });

  s.on('connect', () => {
    socketConnected.value = true;
  });

  s.on('disconnect', () => {
    socketConnected.value = false;
  });

  attachEvents(s);
  return s;
}

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * Call once (e.g. in App.vue or any composable). Idempotent — returns the
 * existing socket if already initialized.
 */
export function initAdminSocket() {
  const accessToken = token.value?.accessToken;
  if (!accessToken) return;

  if (_socket) {
    // Already connected — nothing to do
    if (_socket.connected) return;
    // Disconnected but socket exists — reconnect
    _socket.connect();
    return;
  }

  _socket = createSocket(accessToken);

  // Re-create the socket when the token changes (login / token refresh)
  watch(
    () => token.value?.accessToken,
    (newToken) => {
      if (!newToken) {
        _socket?.disconnect();
        _socket = null;
        socketConnected.value = false;
        return;
      }
      // Token refreshed — reconnect with new auth
      _socket?.disconnect();
      _socket = createSocket(newToken);
    },
  );
}

/**
 * Subscribe to one or more admin events. Returns an unsubscribe function.
 *
 * @example
 * const unsub = onAdminEvent(['store:created', 'store:updated'], (e) => {
 *   queryClient.invalidateQueries({ queryKey: ['analytics-store-stats'] })
 * })
 * onUnmounted(unsub)
 */
export function onAdminEvent(
  events: EventName | EventName[],
  cb: Listener,
): () => void {
  const names = Array.isArray(events) ? events : [events];
  names.forEach((name) => {
    if (!_listeners.has(name)) _listeners.set(name, new Set());
    _listeners.get(name)!.add(cb);
  });
  return () => {
    names.forEach((name) => _listeners.get(name)?.delete(cb));
  };
}
